import { useState, useEffect } from 'react';
import { ArrowLeft, Package, Clock, MapPin, User, Star, Calendar, MessageSquare, LogOut, TrendingUp, Phone, Mail, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import EnhancedBookingModal from '@/components/EnhancedBookingModal';
import ChatWidget from '@/components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [activeBookingForChat, setActiveBookingForChat] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('barbergo_user');
    if (!savedUser) {
      navigate('/');
      toast.error('Please sign in to view your bookings');
      return;
    }
    setUser(JSON.parse(savedUser));
    fetchBookings();
    
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBookings = async () => {
    try {
      const savedUser = localStorage.getItem('barbergo_user');
      if (!savedUser) return;
      
      const userData = JSON.parse(savedUser);
      
      const response = await axios.get(`${API}/bookings`);
      const allBookings = response.data;
      
      // Filter by email, phone, or name (in case booking was made without email)
      const userBookings = allBookings.filter(b => 
        b.customer_email === userData.email || 
        b.customer_phone === userData.phone ||
        b.customer_name === userData.name
      );
      
      setBookings(userBookings);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=cancelled`);
      toast.success('Booking cancelled successfully');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  const submitReview = async () => {
    if (!selectedBooking || !selectedBooking.barber_id) {
      toast.error('No barber assigned to review');
      return;
    }

    try {
      await axios.post(`${API}/reviews`, {
        booking_id: selectedBooking.id,
        barber_id: selectedBooking.barber_id,
        customer_name: user.name || user.email.split('@')[0],
        rating: reviewData.rating,
        comment: reviewData.comment
      });
      
      toast.success('Review submitted successfully!');
      setShowReviewModal(false);
      setReviewData({ rating: 5, comment: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('barbergo_user');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'requested': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'accepted': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'pending': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'confirmed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'in_progress': return 'bg-violet-500/10 text-violet-500 border-violet-500/20';
      case 'completed': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'cancelled': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'requested': return <Clock className="w-5 h-5" />;
      case 'accepted': return <Package className="w-5 h-5" />;
      case 'pending': return <Clock className="w-5 h-5" />;
      case 'confirmed': return <Package className="w-5 h-5" />;
      case 'in_progress': return <TrendingUp className="w-5 h-5" />;
      case 'completed': return <Star className="w-5 h-5 fill-current" />;
      default: return <Calendar className="w-5 h-5" />;
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'requested': return 'WAITING FOR BARBER';
      case 'accepted': return 'BARBER ASSIGNED';
      default: return status.toUpperCase().replace('_', ' ');
    }
  };

  const getFilteredBookings = () => {
    if (activeTab === 'all') return bookings;
    if (activeTab === 'active') {
      return bookings.filter(b => ['requested', 'accepted', 'pending', 'confirmed', 'in_progress'].includes(b.status));
    }
    if (activeTab === 'completed') {
      return bookings.filter(b => b.status === 'completed');
    }
    return bookings;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00C167] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-[var(--text-primary)] text-xl">Loading your bookings...</div>
        </div>
      </div>
    );
  }

  const totalSpent = bookings.reduce((sum, b) => sum + (b.estimated_price || 0), 0);
  const activeBookings = bookings.filter(b => ['pending', 'confirmed', 'in_progress'].includes(b.status)).length;
  const completedBookings = bookings.filter(b => b.status === 'completed').length;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] dark:bg-[#0A0A0A]" data-testid="customer-dashboard">
      {/* Enhanced Header */}
      <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border-b border-[var(--border-color)] sticky top-0 z-40 backdrop-blur-xl">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Home
              </Button>
              
              {user && (
                <div className="hidden md:flex items-center gap-3 px-4 py-2 rounded-lg bg-[var(--bg-primary)] dark:bg-[#0A0A0A] border border-[var(--border-color)]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00C167] to-[#009E54] flex items-center justify-center text-white font-bold">
                    {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-[var(--text-primary)] text-sm">{user.name || user.email}</div>
                    <div className="text-xs text-[var(--text-secondary)]">Customer Account</div>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container py-8">
        {/* Page Title */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black tracking-tight text-[var(--text-primary)] mb-2">My Bookings</h1>
            <p className="text-[var(--text-secondary)] text-lg">Track your appointments and manage your grooming services</p>
          </div>
          <Button
            onClick={() => setShowBookingModal(true)}
            className="bg-[#00C167] hover:bg-[#009E54] text-white font-semibold h-12 px-6"
            data-testid="new-booking-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Book New Service
          </Button>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)] rounded-xl p-6 hover:border-[#00C167] transition-all">
            <Package className="w-8 h-8 text-[var(--text-secondary)] mb-3" />
            <div className="text-3xl font-black text-[var(--text-primary)] mb-1">{bookings.length}</div>
            <div className="text-sm text-[var(--text-secondary)]">Total Bookings</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-blue-500/20 rounded-xl p-6 hover:border-blue-500 transition-all">
            <Clock className="w-8 h-8 text-blue-500 mb-3" />
            <div className="text-3xl font-black text-blue-500 mb-1">{activeBookings}</div>
            <div className="text-sm text-[var(--text-secondary)]">Active Orders</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-emerald-500/20 rounded-xl p-6 hover:border-emerald-500 transition-all">
            <Star className="w-8 h-8 text-emerald-500 mb-3" />
            <div className="text-3xl font-black text-emerald-500 mb-1">{completedBookings}</div>
            <div className="text-sm text-[var(--text-secondary)]">Completed</div>
          </div>

          <div className="bg-gradient-to-br from-[#00C167] to-[#009E54] rounded-xl p-6 text-white shadow-lg shadow-[#00C167]/20">
            <TrendingUp className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-black mb-1">₦{totalSpent.toLocaleString()}</div>
            <div className="text-sm opacity-90">Total Spent</div>
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)] rounded-2xl overflow-hidden shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b border-[var(--border-color)] px-6 py-4 bg-[var(--bg-primary)] dark:bg-[#0A0A0A]">
              <TabsList className="w-full justify-start gap-2">
                <TabsTrigger value="all" data-testid="tab-all" className="data-[state=active]:bg-[#00C167] data-[state=active]:text-white">
                  All <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{bookings.length}</span>
                </TabsTrigger>
                <TabsTrigger value="active" data-testid="tab-active" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  Active <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{activeBookings}</span>
                </TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                  Completed <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{completedBookings}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value={activeTab} className="p-6">
              {getFilteredBookings().length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-[var(--bg-primary)] dark:bg-[#0A0A0A] border-2 border-dashed border-[var(--border-color)] flex items-center justify-center mx-auto mb-4">
                    <Package className="w-10 h-10 text-[var(--text-secondary)]" />
                  </div>
                  <p className="text-[var(--text-secondary)] text-lg font-medium mb-2">No bookings found</p>
                  <p className="text-[var(--text-secondary)] text-sm mb-6">Start your grooming journey today</p>
                  <Button
                    onClick={() => navigate('/')}
                    className="bg-[#00C167] hover:bg-[#009E54] text-white"
                  >
                    Book Your First Service
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {getFilteredBookings().map(booking => (
                    <div
                      key={booking.id}
                      data-testid={`booking-card-${booking.id}`}
                      className="group border-2 border-[var(--border-color)] rounded-xl p-6 hover:border-[#00C167] hover:shadow-lg transition-all bg-[var(--bg-primary)] dark:bg-[#0A0A0A]"
                    >
                      <div className="flex flex-col lg:flex-row gap-6">
                        {/* Left: Service Info */}
                        <div className="flex-1 space-y-4">
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-3 rounded-lg ${getStatusColor(booking.status)}`}>
                                {getStatusIcon(booking.status)}
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-[var(--text-primary)]">{booking.service_name}</h3>
                                <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {booking.booking_date}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {booking.booking_time}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(booking.status)}`}>
                              {getStatusLabel(booking.status)}
                            </span>
                          </div>

                          {/* Details Grid */}
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)]">
                              <div className="text-xs text-[var(--text-secondary)] mb-2 uppercase tracking-wide">Location</div>
                              <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-[#00C167] mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-[var(--text-primary)] font-medium">{booking.address}</span>
                              </div>
                              <div className="mt-2 text-xs text-[var(--text-secondary)] capitalize flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-[#00C167]"></span>
                                {booking.location_type} service
                              </div>
                            </div>

                            <div className="p-4 rounded-lg bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)]">
                              <div className="text-xs text-[var(--text-secondary)] mb-2 uppercase tracking-wide">Your Barber</div>
                              {booking.barber_name ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-[#00C167]/20 flex items-center justify-center text-[#00C167] font-bold text-sm">
                                    {booking.barber_name.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="text-sm text-[var(--text-primary)] font-medium">{booking.barber_name}</div>
                                    {booking.barber_eta && (
                                      <div className="text-xs text-[#00C167] flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        ETA: {booking.barber_eta} min
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                                    <Clock className="w-4 h-4 text-amber-500 animate-pulse" />
                                  </div>
                                  <div>
                                    <div className="text-sm text-amber-500 font-medium">Waiting for barber...</div>
                                    <div className="text-xs text-[var(--text-secondary)]">
                                      A barber will accept your request soon
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Progress Bar */}
                          {['requested', 'accepted', 'pending', 'confirmed', 'in_progress'].includes(booking.status) && (
                            <div className="p-4 rounded-lg bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)]">
                              <div className="flex items-center justify-between text-xs text-[var(--text-secondary)] mb-2">
                                <span className="font-semibold">Booking Progress</span>
                                <span className="font-mono">
                                  {booking.status === 'requested' ? '10%' : 
                                   booking.status === 'accepted' ? '30%' : 
                                   booking.status === 'confirmed' ? '50%' : 
                                   booking.status === 'in_progress' ? '75%' : '25%'}
                                </span>
                              </div>
                              <div className="h-2 bg-[var(--bg-primary)] dark:bg-[#0A0A0A] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-[#00C167] to-[#009E54] transition-all duration-500"
                                  style={{ 
                                    width: booking.status === 'requested' ? '10%' : 
                                           booking.status === 'accepted' ? '30%' : 
                                           booking.status === 'confirmed' ? '50%' : 
                                           booking.status === 'in_progress' ? '75%' : '25%' 
                                  }}
                                ></div>
                              </div>
                              <div className="flex justify-between text-xs text-[var(--text-secondary)] mt-2">
                                <span className={['requested', 'accepted', 'confirmed', 'in_progress'].includes(booking.status) ? 'text-[#00C167]' : ''}>Requested</span>
                                <span className={['accepted', 'confirmed', 'in_progress'].includes(booking.status) ? 'text-[#00C167]' : ''}>Barber assigned</span>
                                <span className={['confirmed', 'in_progress'].includes(booking.status) ? 'text-[#00C167]' : ''}>On the way</span>
                                <span className={booking.status === 'in_progress' ? 'text-[#00C167]' : ''}>In service</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right: Price & Actions */}
                        <div className="lg:min-w-[200px] flex flex-col gap-4">
                          <div className="p-4 rounded-xl bg-gradient-to-br from-[#00C167] to-[#009E54] text-white text-center">
                            <div className="text-xs opacity-80 mb-1">Total Amount</div>
                            <div className="text-3xl font-black">₦{booking.estimated_price?.toLocaleString()}</div>
                          </div>

                          <div className="flex-1 space-y-2">
                            {booking.status === 'completed' && (
                              <Button
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowReviewModal(true);
                                }}
                                className="w-full bg-[#00C167] hover:bg-[#009E54] text-white"
                                data-testid={`review-${booking.id}`}
                              >
                                <Star className="w-4 h-4 mr-2" />
                                Leave Review
                              </Button>
                            )}
                            {['requested', 'accepted', 'pending', 'confirmed'].includes(booking.status) && (
                              <Button
                                onClick={() => cancelBooking(booking.id)}
                                variant="outline"
                                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                                data-testid={`cancel-${booking.id}`}
                              >
                                Cancel Booking
                              </Button>
                            )}
                            {['accepted', 'confirmed', 'in_progress'].includes(booking.status) && booking.barber_name && (
                              <Button
                                onClick={() => setActiveBookingForChat(booking)}
                                variant="outline"
                                className="w-full border-[#00C167]/30 text-[#00C167] hover:bg-[#00C167]/10"
                                data-testid={`chat-${booking.id}`}
                              >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Chat with Barber
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              className="w-full text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            >
                              View Full Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="review-modal">
          <div className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Rate Your Experience</h3>
            <p className="text-[var(--text-secondary)] mb-6">How was your service with {selectedBooking.barber_name}?</p>
            
            <div className="space-y-4">
              <div>
                <Label className="text-[var(--text-primary)] mb-3 block text-center">Your Rating</Label>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewData({...reviewData, rating: star})}
                      className="text-5xl transition-transform hover:scale-110"
                    >
                      {star <= reviewData.rating ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="review-comment" className="text-[var(--text-primary)]">Your Feedback</Label>
                <Textarea
                  id="review-comment"
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({...reviewData, comment: e.target.value})}
                  placeholder="Tell us about your experience..."
                  className="mt-2 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)]"
                  rows={4}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowReviewModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={submitReview}
                className="flex-1 bg-[#00C167] hover:bg-[#009E54] text-white"
              >
                Submit Review
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Booking Modal */}
      <EnhancedBookingModal
        open={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        onSuccess={() => {
          fetchBookings();
          setShowBookingModal(false);
        }}
      />

      {/* Chat Widget */}
      {activeBookingForChat && user && (
        <ChatWidget
          booking={activeBookingForChat}
          user={user}
          userType="customer"
        />
      )}
    </div>
  );
};

export default CustomerDashboard;